import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useReducer,
  useState,
} from "react";
import { type Artifact, isPreviewable } from "../lib/canvas";

type ViewMode = "code" | "preview";

/** Resizable drawer width — defaults to the Sources width; drag to widen. */
const DEFAULT_WIDTH = 380;
const MIN_WIDTH = 360;
const MAX_WIDTH = 960;
const WIDTH_KEY = "anyai-canvas-width";

/** Clamp to [MIN, MAX] and never wider than leaves room for sidebar + content. */
function clampWidth(px: number): number {
  const ceiling =
    typeof window !== "undefined" ? Math.min(MAX_WIDTH, window.innerWidth - 240) : MAX_WIDTH;
  return Math.max(MIN_WIDTH, Math.min(Math.max(ceiling, MIN_WIDTH), Math.round(px)));
}

function initialWidth(): number {
  try {
    const stored = Number(localStorage.getItem(WIDTH_KEY));
    if (stored) return clampWidth(stored);
  } catch {
    /* localStorage unavailable */
  }
  return DEFAULT_WIDTH;
}

interface CanvasState {
  isOpen: boolean;
  artifact: Artifact | null;
  viewMode: ViewMode;
}

interface CanvasContextValue extends CanvasState {
  openArtifact: (artifact: Artifact) => void;
  close: () => void;
  setViewMode: (mode: ViewMode) => void;
  /** Update the open artifact's code as it streams in (no-op if not the active one). */
  syncContent: (id: string, code: string) => void;
  /** Current drawer width in px. */
  width: number;
  /** Set the drawer width (clamped + persisted). */
  setWidth: (px: number) => void;
}

type Action =
  | { type: "open"; artifact: Artifact }
  | { type: "close" }
  | { type: "setViewMode"; mode: ViewMode }
  | { type: "sync"; id: string; code: string };

function reducer(state: CanvasState, action: Action): CanvasState {
  switch (action.type) {
    case "open":
      return {
        isOpen: true,
        artifact: action.artifact,
        viewMode: isPreviewable(action.artifact.type) ? "preview" : "code",
      };
    case "close":
      return { ...state, isOpen: false };
    case "setViewMode":
      return { ...state, viewMode: action.mode };
    case "sync":
      if (!state.artifact || state.artifact.id !== action.id) return state;
      if (state.artifact.code === action.code) return state;
      return { ...state, artifact: { ...state.artifact, code: action.code } };
  }
}

const CanvasContext = createContext<CanvasContextValue | null>(null);

/**
 * Owns the Canvas panel state for the chat subtree: which artifact is open and
 * whether code or preview is shown. Code blocks open artifacts into it; the
 * panel reads this state. Lives in `features/chat` because artifacts come from
 * chat code blocks.
 */
export function CanvasProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, {
    isOpen: false,
    artifact: null,
    viewMode: "preview",
  });
  const [width, setWidthState] = useState<number>(initialWidth);

  const openArtifact = useCallback((artifact: Artifact) => dispatch({ type: "open", artifact }), []);
  const close = useCallback(() => dispatch({ type: "close" }), []);
  const setViewMode = useCallback((mode: ViewMode) => dispatch({ type: "setViewMode", mode }), []);
  const syncContent = useCallback((id: string, code: string) => dispatch({ type: "sync", id, code }), []);
  const setWidth = useCallback((px: number) => {
    const w = clampWidth(px);
    setWidthState(w);
    try {
      localStorage.setItem(WIDTH_KEY, String(w));
    } catch {
      /* localStorage unavailable */
    }
  }, []);

  const value = useMemo<CanvasContextValue>(
    () => ({ ...state, width, openArtifact, close, setViewMode, syncContent, setWidth }),
    [state, width, openArtifact, close, setViewMode, syncContent, setWidth],
  );

  return <CanvasContext.Provider value={value}>{children}</CanvasContext.Provider>;
}

/** Canvas controls, or null when rendered outside a CanvasProvider. */
export function useCanvas(): CanvasContextValue | null {
  return useContext(CanvasContext);
}
